import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { FindPersonContactMode } from "@signalist/shared-types";

import { ModeCard } from "./FindPersonModeSelector";

interface FindPersonContactModeSelectorProps {
	value: FindPersonContactMode;
	onChange: (mode: FindPersonContactMode) => void;
	/** The line under the title; by default, where the list's setting can be changed later. */
	hint?: string;
	/**
	 * The "Use my contacts first" card's line; by default, the list run's copy, where reuse of
	 * recent contacts is free. The bulk run charges them, so its dialog passes its own (PREC-4625).
	 */
	contactsFirstDescription?: string;
}

/**
 * "Use my contacts first" or "Find new people only" (PREC-3899): whether a company-based list's
 * Find person may hand back people already in the workspace's Contacts. Shown in the Find person
 * step and in List Settings, where it replaces "Only new leads" on the lists that find people at
 * their companies (`usesFindPersonWaterfall`).
 */
export const FindPersonContactModeSelector: FunctionComponent<
	FindPersonContactModeSelectorProps
> = ({ value, onChange, hint, contactsFirstDescription }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.detailsStep.contactMode.${key}`);

	return (
		<section
			aria-label={ts("title")}
			className="mb-5 flex flex-col gap-3.5 rounded-xl border border-border-light p-5"
		>
			<div>
				<h2 className="text-base font-bold text-text">{ts("title")}</h2>
				<div className="mt-1 text-xs text-gray-700">{hint ?? ts("hint")}</div>
			</div>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup">
				<ModeCard
					badge={ts("default")}
					description={contactsFirstDescription ?? ts("contactsFirstDescription")}
					selected={value !== FindPersonContactMode.NEW_PEOPLE_ONLY}
					testId="find-person-contact-mode-contacts-first"
					title={ts("contactsFirst")}
					onSelect={() => onChange(FindPersonContactMode.CONTACTS_FIRST)}
				/>
				<ModeCard
					description={ts("newPeopleOnlyDescription")}
					selected={value === FindPersonContactMode.NEW_PEOPLE_ONLY}
					testId="find-person-contact-mode-new-people-only"
					title={ts("newPeopleOnly")}
					onSelect={() => onChange(FindPersonContactMode.NEW_PEOPLE_ONLY)}
				/>
			</div>
		</section>
	);
};
